import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { useJarvisContext } from '../../../hooks/useJarvisContext';
import { usePreferences } from '../../../contexts/PreferencesContext';
import { useCars } from '../../../hooks/useCarData';
import { useAuth } from '../../../contexts/AuthContext';

interface Message {
    id: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    payload?: any;
    created_at: string;
    is_read: boolean;
    is_internal?: boolean;
    error?: boolean;
}

interface JarvisContextType {
    isOpen: boolean;
    toggleChat: () => void;
    messages: Message[];
    sendMessage: (content: string) => Promise<void>;
    isLoading: boolean;
    unreadCount: number;
    markAsRead: () => Promise<void>;
    loadMore: () => Promise<void>;
    hasMore: boolean;
    totalCount: number;
    isFetchingHistory: boolean;
    executeAction: (action: any) => Promise<any>;
    currentStatus: string | null;
}

const JarvisContext = createContext<JarvisContextType | undefined>(undefined);

export function JarvisProvider({ children }: { children: React.ReactNode }) {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<Message[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [isFetchingHistory, setIsFetchingHistory] = useState(false);
    const [currentStatus, setCurrentStatus] = useState<string | null>(null);
    const context = useJarvisContext();
    const { user, isAdmin } = useAuth();
    const { preferences } = usePreferences();
    const { data: cars } = useCars(user?.id);

    const markAsRead = useCallback(async () => {
        const unreadMessages = messages.filter(m => !m.is_read);
        if (unreadMessages.length === 0) return;
        setMessages(prev => prev.map(m => ({ ...m, is_read: true })));
        setUnreadCount(0);
        const { data: { user } } = await supabase.auth.getUser();
        if (user) await supabase.from('ai_chat_messages').update({ is_read: true }).eq('user_id', user.id).eq('is_read', false);
    }, [messages]);

    useEffect(() => {
        const fetchHistory = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            const { data, error, count } = await supabase.from('ai_chat_messages').select('*', { count: 'exact' }).eq('is_internal', false).order('created_at', { ascending: false }).limit(50);
            if (!error && data) {
                const latestMsg = data[0];
                const isOlderThan2Days = latestMsg
                    ? (new Date().getTime() - new Date(latestMsg.created_at).getTime()) > 2 * 24 * 60 * 60 * 1000
                    : false;

                if (isOlderThan2Days) {
                    setMessages([]);
                    setHasMore(true);
                } else {
                    setMessages(data.reverse());
                    setHasMore(data.length === 50);
                }
                setTotalCount(count || 0);
                setUnreadCount(data.filter((m: any) => !m.is_read && m.role === 'assistant').length);
            }
        };
        fetchHistory();
    }, []);

    const loadMore = useCallback(async () => {
        if (!hasMore || isFetchingHistory) return;
        setIsFetchingHistory(true);
        const oldestCreatedAt = messages.length > 0 ? messages[0].created_at : null;
        let query = supabase.from('ai_chat_messages').select('*').eq('is_internal', false).order('created_at', { ascending: false }).limit(50);
        if (oldestCreatedAt) {
            query = query.lt('created_at', oldestCreatedAt);
        }
        const { data, error } = await query;
        if (!error && data) {
            setHasMore(data.length === 50);
            setMessages(prev => [...data.reverse(), ...prev]);
        }
        setIsFetchingHistory(false);
    }, [messages, hasMore, isFetchingHistory]);

    const toggleChat = useCallback(() => {
        setIsOpen(prev => !prev);
    }, []);

    const executeAction = async (action: any) => {
        try {
            console.log('[JARVIS ACTION]', action);
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return null;
            const carId = context.carId;
            const car = cars?.find(c => c.id === carId);
            const canManage = car?.user_id === user.id || car?.allowed_emails?.includes(user.email || '') || isAdmin;
            if (!canManage) return null;

            const { type, data, message_id } = action;
            let result: any = null;

            switch (type) {
                case 'upsert_log': {
                    const { id, date, location_name, total_amount, type_id, parts, odometer, car_value, is_initial_purchase } = data;
                    const is_temporary = action.is_temporary ?? data.is_temporary ?? true;
                    
                    let locationId = null;
                    if (location_name) {
                        const { data: locs } = await supabase.from('locations').select('id, name').eq('user_id', user.id);
                        const match = locs?.find(l => l.name.toLowerCase().includes(location_name.toLowerCase()));
                        if (match) locationId = match.id;
                        else {
                            const { data: newLoc } = await supabase.from('locations').insert({ user_id: user.id, name: location_name, is_temporary: is_temporary }).select().single();
                            if (newLoc) locationId = newLoc.id;
                        }
                    }

                    let logId = id;
                    const logPayload = { 
                        car_id: carId, 
                        user_id: user.id, 
                        date: date || new Date().toISOString(), 
                        location_id: locationId, 
                        mileage: odometer || action.data.mileage,
                        car_value: car_value,
                        is_initial_purchase_entry: is_initial_purchase,
                        is_temporary: is_temporary 
                    };

                    if (logId) {
                        // UPDATE
                        await supabase.from('log_entries').update(logPayload).eq('id', logId);
                        // Clear existing nested data to re-insert fresh
                        await supabase.from('expenses').delete().eq('log_entry_id', logId);
                        await supabase.from('log_entry_parts').delete().eq('log_entry_id', logId);
                    } else {
                        // INSERT
                        const { data: newLog, error: logErr } = await supabase.from('log_entries').insert(logPayload).select().single();
                        if (logErr) throw logErr;
                        logId = newLog.id;
                    }

                    // 2. Create primary expense record (Detail)
                    const typeToKey: any = { 1: 'fuel', 2: 'car_wash', 3: 'repairs', 4: 'maintenance', 5: 'parts', 6: 'insurance', 7: 'inspection', 8: 'tyres', 9: 'parking', 10: 'electricity', 11: 'taxes', 12: 'other', 13: 'info', 14: 'tolls' };
                    const { data: insertedExpenses } = await supabase.from('expenses').insert({ 
                        log_entry_id: logId, 
                        user_id: user.id, 
                        date: logPayload.date, 
                        car_id: carId,
                        type: typeToKey[type_id] || typeToKey[parseInt(String(type_id).replace(/\D/g, ''))] || 'other', 
                        amount: total_amount || action.data.price || 0,
                        title: '' 
                    }).select();
                    
                    // 3. Handle Parts
                    if (parts?.length > 0 && insertedExpenses?.[0]) {
                        for (const p of parts) {
                            const { data: ex } = await supabase.from('spare_parts')
                                .select('id')
                                .eq('user_id', user.id)
                                .eq('car_id', carId)
                                .eq('name', p.name)
                                .maybeSingle();
                            
                            let pId = ex?.id;
                            if (!pId) {
                                const { data: np } = await supabase.from('spare_parts').insert({ 
                                    user_id: user.id, 
                                    car_id: carId, 
                                    name: p.name, 
                                    part_code: p.part_code || p.part_number, 
                                    manufacturer: p.manufacturer, 
                                    is_temporary: is_temporary 
                                }).select().single();
                                pId = np?.id;
                            } else if (p.manufacturer || p.part_code) {
                                // Update existing part metadata if provided
                                await supabase.from('spare_parts').update({
                                    manufacturer: p.manufacturer,
                                    part_code: p.part_code || p.part_number
                                }).eq('id', pId);
                            }

                            if (pId) {
                                await supabase.from('log_entry_parts').insert({ 
                                    log_entry_id: logId, 
                                    spare_part_id: pId, 
                                    quantity: p.quantity || 1, 
                                    price_per_unit: p.price_per_unit || p.price || p.amount || 0, 
                                    purchased_at: logPayload.date 
                                });
                            }
                        }
                    }

                    // Fetch enriched for UI
                    const { data: fullEntry } = await supabase
                        .from('log_entries')
                        .select(`*, expenses (*), location:locations (*), log_entry_parts(spare_part_id, spare_parts(name, manufacturer))`)
                        .eq('id', logId)
                        .single();

                    result = { ...fullEntry, type: 'log', is_temporary: is_temporary, car_slug: car?.slug };
                    break;
                }
                case 'upsert_note': {
                    const { data: n } = await supabase.from('notes').insert({ user_id: user.id, car_id: context.carId, content: data.content, is_temporary: true }).select().single();
                    result = { ...n, type: 'note', is_temporary: true };
                    break;
                }
                case 'upsert_part': {
                    const { data: p } = await supabase.from('spare_parts').insert({ user_id: user.id, car_id: context.carId, name: data.name, part_number: data.part_number, price: data.price, is_temporary: true }).select().single();
                    result = { ...p, type: 'part', is_temporary: true };
                    break;
                }
                case 'upsert_location': {
                    const { data: l } = await supabase.from('locations').insert({ user_id: user.id, name: data.name, address: data.address, is_temporary: true }).select().single();
                    result = { ...l, type: 'location', is_temporary: true };
                    break;
                }
                case 'upsert_reminder': {
                    const { data: r } = await supabase.from('reminders').insert({ 
                        user_id: user.id, 
                        car_id: context.carId, 
                        name: data.name, 
                        description: data.description,
                        remind_date: data.remind_date,
                        is_active: true,
                        is_temporary: true 
                    }).select().single();
                    result = { ...r, type: 'reminder', is_temporary: true };
                    break;
                }
                case 'confirm': {
                    const tableMap: any = { log: 'log_entries', note: 'notes', part: 'spare_parts', location: 'locations', reminder: 'reminders' };
                    await supabase.from(tableMap[action.target_type] || 'log_entries').update({ is_temporary: false }).eq('id', action.target_id);
                    result = { confirmed: true, id: action.target_id };
                    break;
                }
                case 'delete': {
                    const tableMap: any = { log: 'log_entries', note: 'notes', part: 'spare_parts', location: 'locations', reminder: 'reminders' };
                    if (action.target_type === 'log') {
                        await supabase.from('expenses').delete().eq('log_entry_id', action.target_id);
                        await supabase.from('log_entry_parts').delete().eq('log_entry_id', action.target_id);
                    }
                    await supabase.from(tableMap[action.target_type] || 'log_entries').delete().eq('id', action.target_id);
                    result = { deleted: true, id: action.target_id };
                    break;
                }
                default: break;
            }

            // Persistence: Update Message Payload in DB if message_id provided and it's a real UUID
            if (message_id && result && (type === 'confirm' || type === 'delete')) {
                // If it's a client-side ID (starts with assistant-), we only update local state
                if (message_id.startsWith('assistant-')) {
                    setMessages(prev => prev.map(m => {
                        if (m.id === message_id && m.payload) {
                            const payload = typeof m.payload === 'string' ? JSON.parse(m.payload) : m.payload;
                            if (payload.links) {
                                payload.links = payload.links.map((link: any) => {
                                    if (link.id === action.target_id) {
                                        return { ...link, is_processed: true, action_status: type === 'confirm' ? 'confirmed' : 'deleted' };
                                    }
                                    return link;
                                });
                                return { ...m, payload: JSON.stringify(payload) };
                            }
                        }
                        return m;
                    }));
                } else {
                    // It's a real DB UUID
                    const { data: msg } = await supabase.from('ai_chat_messages').select('payload').eq('id', message_id).single();
                    if (msg?.payload) {
                        const payload = typeof msg.payload === 'string' ? JSON.parse(msg.payload) : msg.payload;
                        if (payload.links) {
                            payload.links = payload.links.map((link: any) => {
                                if (link.id === action.target_id) {
                                    return { ...link, is_processed: true, action_status: type === 'confirm' ? 'confirmed' : 'deleted' };
                                }
                                return link;
                            });
                            await supabase.from('ai_chat_messages').update({ payload: JSON.stringify(payload) }).eq('id', message_id);
                            setMessages(prev => prev.map(m => m.id === message_id ? { ...m, payload: JSON.stringify(payload) } : m));
                        }
                    }
                }
            }

            return result;
        } catch (err) { console.error('Jarvis action failed:', err); return null; }
    };

    const sendMessage = async (content: string, retryId?: string) => {
        setIsLoading(true);
        setCurrentStatus(null);
        let targetId = retryId;
        
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error("Not authenticated");

            const userMsg: Message = { id: retryId || `user-${Date.now()}`, role: 'user', content, created_at: new Date().toISOString(), is_read: true };
            if (!retryId) setMessages(prev => [...prev, userMsg]);
            targetId = userMsg.id;

            const assistantMsgId = `assistant-${Date.now()}`;
            let accumulatedContent = "";
            let isFirstChunk = true;

            // Get history currently loaded in UI, up to 50 messages
            const uiHistory = messages.slice(-50).map(m => ({
                role: m.role,
                content: m.content
            }));

            // Call Jarvis directly with data inclusion enabled from start
            let response = await fetch("https://mycarcosts-jarvis.gtreulands.workers.dev/chat", {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${session.access_token}` },
                body: JSON.stringify({ message: content, carId: context.carId, contextUrl: context.currentUrl, language: preferences?.language || 'en', isOpen, includeData: true, userRole: isAdmin ? 'Super Admin' : 'User', history: uiHistory })
            });

            if (!response.ok) throw new Error("Jarvis offline");
            
            let reader = response.body?.getReader();
            const decoder = new TextDecoder();
            
            if (reader) {
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    const chunk = decoder.decode(value, { stream: true });
                    accumulatedContent += chunk;

                    const progressMatch = accumulatedContent.match(/\[PROGRESS:[\s\S]*?\]/g);
                    if (progressMatch) setCurrentStatus(progressMatch[progressMatch.length - 1].replace(/\[PROGRESS:\s*(.*?)\]/i, '$1'));

                    if (isFirstChunk) {
                        setMessages(prev => [...prev, { id: assistantMsgId, role: 'assistant', content: accumulatedContent, created_at: new Date().toISOString(), is_read: true }]);
                        isFirstChunk = false;
                    } else {
                        setMessages(prev => prev.map(m => m.id === assistantMsgId ? { ...m, content: accumulatedContent } : m));
                    }
                }
            }
            setCurrentStatus(null);

            // --- AFTER STREAM FINISHES ---
            // 1. Extract Actions from the DATA part
            const dataParts = accumulatedContent.split('|---DATA---|');
            const dataContent = dataParts.length > 1 ? dataParts[1] : accumulatedContent;
            
            console.log('[JARVIS] Processing data part:', dataContent);

            // Balanced bracket parsing for actions
            const actionMatches: string[] = [];
            let searchPos = 0;
            while (true) {
                const actionStart = dataContent.indexOf('[ACTION:', searchPos);
                if (actionStart === -1) break;
                
                let bracketCount = 0;
                let actionEnd = -1;
                for (let i = actionStart; i < dataContent.length; i++) {
                    if (dataContent[i] === '[') bracketCount++;
                    if (dataContent[i] === ']') {
                        bracketCount--;
                        if (bracketCount === 0) {
                            actionEnd = i;
                            break;
                        }
                    }
                }
                
                if (actionEnd === -1) break;
                actionMatches.push(dataContent.substring(actionStart, actionEnd + 1));
                searchPos = actionEnd + 1;
            }
            
            if (actionMatches) {
                console.log('[JARVIS] Found action matches:', actionMatches.length);
                for (const m of actionMatches) {
                    try {
                        const jsonStr = m.substring(8, m.length - 1);
                        const action = JSON.parse(jsonStr);
                        const entry = await executeAction(action);
                        if (entry) {
                            setMessages(prev => prev.map(msg => msg.id === assistantMsgId ? { 
                                ...msg, 
                                payload: { ...msg.payload, links: [...(msg.payload?.links || []), { ...entry, alias: 'NEW' }] } 
                            } : msg));
                            
                            const { data: dbMsg } = await supabase.from('ai_chat_messages').select('id, payload').eq('user_id', session.user.id).eq('role', 'assistant').order('created_at', { ascending: false }).limit(1).single();
                            if (dbMsg) {
                                const newPayload = { ...(dbMsg.payload || {}), links: [...(dbMsg.payload?.links || []), { ...entry, alias: 'NEW' }] };
                                await supabase.from('ai_chat_messages').update({ payload: newPayload }).eq('id', dbMsg.id);
                            }
                        }
                    } catch (e) { console.error('[JARVIS] Action parse error:', e, m); }
                }
            }
        } catch (error) {
            console.error("Jarvis Error:", error);
            setMessages(prev => prev.map(m => m.id === targetId ? { ...m, error: true } : m));
        } finally {
            setIsLoading(false);
            setCurrentStatus(null);
        }
    };

    return (
        <JarvisContext.Provider value={{ isOpen, toggleChat, messages, sendMessage, isLoading, unreadCount, markAsRead, loadMore, hasMore, totalCount, isFetchingHistory, executeAction, currentStatus }}>
            {children}
        </JarvisContext.Provider>
    );
}

export const useJarvis = () => {
    const context = useContext(JarvisContext);
    if (!context) throw new Error("useJarvis must be used within JarvisProvider");
    return context;
};
